import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Star, Upload } from 'lucide-react';
import { Abas } from '../../componentes/abas';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { aceitoDaUrl, uuidDaUrl } from '../../componentes/filtros-lista';
import { Selecao } from '../../componentes/formulario';
import { BotaoModeloCsv } from '../../componentes/importacao';
import { BotaoLixeira } from '../../componentes/lixeira';
import { Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { TextoCortado } from '../../componentes/texto-cortado';
import { BarraVerticaisLote, FiltroVertical, SeloVerticais, useEmpresasDoCatalogo, useSelecaoVerticais } from '../../componentes/verticais';
import { get } from '../../nucleo/api';
import { data, email, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { textoInadimplencia, type MarcaInadimplencia } from '../../nucleo/inadimplencia';
import { useSessao } from '../../nucleo/sessao';
import { FormParceiro } from './FormParceiro';
import { CAMINHO_IMPORTAR_PESSOAS, ImportarCsv } from './ImportarCsv';
import { nomePais } from './paises';
import { documentoDoPais } from '../../nucleo/formato';
import { FILTROS_PAPEL, PAPEIS_NOVO, papelDoFiltro, SITUACOES_DA_OPERACAO, telefoneFormatado, type FiltroPapel, type PapelNovo, type Parceiro } from './tipos';

export function Estrelas({ n }: { n: number }) {
  return (
    <span className="inline-flex shrink-0 gap-px" role="img" aria-label={`${n}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`size-3 ${i <= n ? 'fill-current text-dourado' : 'text-tinta-3'}`} aria-hidden />
      ))}
    </span>
  );
}

/**
 * Selos de papel do cadastro (cliente, fornecedor, parceiro comercial). Trilha 4: do lado cliente, o lead e o ex-cliente
 * aparecem com a situação no lugar de "Cliente" (o cliente da carteira segue com o selo de sempre).
 */
export function SelosPapel({ p }: { p: Pick<Parceiro, 'e_cliente' | 'e_fornecedor' | 'e_parceiro'> & Partial<Pick<Parceiro, 'situacao_comercial'>> }) {
  const { t } = useTranslation('cadastros');
  const situacao = p.situacao_comercial ?? 'cliente';
  return (
    <>
      {p.e_cliente && (situacao === 'cliente' ? <Selo cor="ciano">{t('papeis.cliente')}</Selo> : <Selo cor={situacao === 'lead' ? 'azul' : 'neutro'}>{t(`situacoes.${situacao}`)}</Selo>)}
      {p.e_fornecedor && <Selo cor="roxo">{t('papeis.fornecedor')}</Selo>}
      {p.e_parceiro && <Selo cor="dourado">{t('papeis.parceiro')}</Selo>}
    </>
  );
}

/**
 * Fase 2 (W19, K4): selo "Inadimplente" da marca derivada do Contas (faturas vencidas em aberto). Sem marca (em dia, sem o
 * Contas ou sem `contas.ver`), nada. O detalhe (quantas, há quantos dias e o valor por moeda) vai no title; o valor passa pelo
 * formatador de dinheiro (modo discreto: só o símbolo).
 */
export function SeloInadimplente({ m }: { m: MarcaInadimplencia | null | undefined }) {
  const { t } = useTranslation();
  if (!m) return null;
  return (
    <span data-inadimplente="" title={textoInadimplencia(m)} className="inline-flex shrink-0">
      <Selo cor="perigo">{t('inadimplencia.selo')}</Selo>
    </span>
  );
}

/** Filtro ativo pela URL (`?papel=`): as rotas antigas (Empresas, Clientes...) chegam aqui com o filtro certo. */
function useFiltro(): [FiltroPapel, (f: FiltroPapel) => void] {
  const [busca, setBusca] = useSearchParams();
  const bruto = busca.get('papel') as FiltroPapel | null;
  const filtro = bruto && FILTROS_PAPEL.includes(bruto) ? bruto : 'todos';
  return [
    filtro,
    (f) =>
      setBusca(
        (atual) => {
          const n = new URLSearchParams(atual);
          if (f === 'todos') n.delete('papel');
          else n.set('papel', f);
          return n;
        },
        { replace: true },
      ),
  ];
}

/** "Novo" pergunta o papel, já marcado pelo filtro ativo (fase 2, W5). */
function EscolherPapel({ aberto, aoFechar, inicial, aoEscolher }: { aberto: boolean; aoFechar: () => void; inicial: PapelNovo; aoEscolher: (p: PapelNovo) => void }) {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const [papel, setPapel] = useState<PapelNovo>(inicial);
  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={t('novo.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" onClick={() => aoEscolher(papel)}>
            {t('novo.continuar')}
          </Botao>
        </>
      }
    >
      <fieldset>
        <legend className="mb-3 text-sm font-medium text-tinta-2">{t('novo.pergunta')}</legend>
        <ul className="divide-y divide-borda rounded-lg border border-borda bg-sup-2">
          {PAPEIS_NOVO.map((p) => (
            <li key={p}>
              <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 text-sm">
                <input type="radio" name="papel-novo" value={p} checked={papel === p} onChange={() => setPapel(p)} className="mt-0.5 size-4 accent-acento" />
                <span className="flex min-w-0 flex-col">
                  <span className="font-medium text-tinta-1">{t(`papeis.${p}`)}</span>
                  <span className="text-xs text-tinta-3">{t(`novo.ajuda.${p}`)}</span>
                </span>
              </label>
            </li>
          ))}
        </ul>
      </fieldset>
    </Modal>
  );
}

/**
 * Fase 2 (W5, L3): "Pessoas e empresas", a aba única no lugar de Empresas, Clientes, Fornecedores e Contatos (era a mesma
 * tabela quatro vezes). Chips de filtro com contagem, "Novo" que pergunta o papel, importação com "Baixar modelo".
 */
export function ListaParceiros() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  const [filtro, setFiltro] = useFiltro();
  const [url] = useSearchParams();
  // Trilha 4: filtro de situação comercial (lead, cliente, ex-cliente); o link do indicador "Clientes ativos" chega com ele.
  const [situacao, setSituacao] = useState(() => aceitoDaUrl(url.get('situacao'), SITUACOES_DA_OPERACAO));
  // V1 verticais (20/10): filtro "Vertical", selo na lista e marcação em lote.
  const [vertical, setVertical] = useState(() => uuidDaUrl(url.get('vertical')));
  const catalogo = useEmpresasDoCatalogo();
  // crm8 (2): a lista da operação não mostra o lead (ele fica só no CRM até o ganho).
  const params = { papel: filtro, situacao: situacao || undefined, vertical: vertical || undefined, operacao: true };
  const lote = useSelecaoVerticais<Parceiro>(JSON.stringify(params), (p) => tn('verticais.selecionar', { nome: nomePessoa(p.nome) }), s.pode('cadastros.editar'));
  const [escolher, setEscolher] = useState(false);
  const [novo, setNovo] = useState<{ papel: PapelNovo; empresa: boolean } | null>(null);
  const [importar, setImportar] = useState(false);
  const contagem = useQuery({ queryKey: ['/cadastros/parceiros', 'contagem', 'operacao'], queryFn: () => get<Record<FiltroPapel, number>>('/cadastros/parceiros/contagem', { operacao: true }) });
  const colunas: Coluna<Parceiro>[] = [
    {
      chave: 'nome',
      rotulo: filtro === 'empresa' ? t('campos.razao') : t('campos.nome'),
      ordenavel: true,
      principal: true,
      // Review r16 visual B4: nome até 15rem e e-mail até 12rem (cortam com o title), para Telefone e Tags não irem para a
      // rolagem da tabela a 1440 depois da coluna Código (W19).
      render: (p) => (
        <span className="flex max-w-60 min-w-0 flex-col">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium" title={nomePessoa(p.nome)}>
              {nomePessoa(p.nome)}
            </span>
            <SeloInadimplente m={p.inadimplencia} />
          </span>
          {p.parent_nome && <TextoCortado className="text-xs text-tinta-3">{[textoLivre(p.cargo, p.nome), nomePessoa(p.parent_nome)].filter(Boolean).join(', ')}</TextoCortado>}
        </span>
      ),
    },
    {
      chave: 'papeis',
      rotulo: t('campos.papeis'),
      oculta: filtro !== 'todos' && filtro !== 'empresa' && filtro !== 'contato',
      render: (p) => (
        <span className="flex flex-wrap gap-1">
          <SelosPapel p={p} />
        </span>
      ),
    },
    // Fase 2 (W19, K6): código do parceiro (a busca também casa o código).
    { chave: 'codigo', rotulo: t('campos.codigo'), ordenavel: true, mono: true, render: (p) => p.codigo ?? '' },
    { chave: 'documento', rotulo: t('campos.documento'), mono: true, render: (p) => documentoDoPais(p.pais, p.documento_tipo, p.documento) },
    { chave: 'email', rotulo: t('campos.email'), render: (p) => <TextoCortado className="block max-w-48">{email(p.email)}</TextoCortado> },
    { chave: 'telefone', rotulo: t('campos.telefone'), mono: true, render: (p) => telefoneFormatado(p.telefone) },
    { chave: 'pais', rotulo: t('campos.pais'), oculta: true, render: (p) => nomePais(p.pais) },
    { chave: 'setor_nome', rotulo: t('campos.setor'), oculta: true },
    {
      chave: 'tags',
      rotulo: t('campos.tags'),
      render: (p) => (
        <span className="flex flex-wrap gap-1">
          {p.tags.map((tag) => (
            <Selo key={tag.id} cor={tag.cor as CorSelo}>
              {tag.nome}
            </Selo>
          ))}
        </span>
      ),
    },
    {
      chave: 'empresas',
      rotulo: tn('verticais.titulo'),
      oculta: (catalogo.data?.empresas.length ?? 0) < 2,
      render: (p) => <SeloVerticais empresas={p.empresas} catalogo={catalogo.data?.empresas ?? []} />,
    },
    { chave: 'ranking_cliente', rotulo: t('campos.ranking'), ordenavel: true, oculta: filtro !== 'cliente', render: (p) => <Estrelas n={p.ranking_cliente} /> },
    { chave: 'origem', rotulo: t('campos.origem'), oculta: true },
    { chave: 'created_at', rotulo: t('campos.criadoEm'), ordenavel: true, oculta: true, mono: true, render: (p) => data(p.created_at) },
  ];
  const total = contagem.data?.[filtro];
  return (
    <>
      {s.pode('cadastros.criar') && (
        <AcaoPrimaria>
          {s.pode('cadastros.excluir') && <BotaoLixeira entidades={['cadastros.parceiro']} />}
          {s.pode('cadastros.importar') && (
            <>
              {/* O modelo traz só os cabeçalhos e uma linha de exemplo fictícia: nenhum dado real, nada a confirmar no modo discreto. */}
              <BotaoModeloCsv caminho={CAMINHO_IMPORTAR_PESSOAS} />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('acoes.importar')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setEscolher(true)}>
            {t('acoes.novoCadastro')}
          </Botao>
        </AcaoPrimaria>
      )}
      <div className="mb-4">
        <Abas
          grupo="cadastros-pessoas"
          variante="chips"
          rotulo={t('filtros.rotulo')}
          ativa={filtro}
          aoMudar={setFiltro}
          itens={FILTROS_PAPEL.map((f) => ({
            chave: f,
            rotulo: (
              <span className="inline-flex items-center gap-1.5">
                {t(`filtros.${f}`)}
                {/* Review r16 visual M4: sem opacidade (com 80% a contagem do chip selecionado caía para 3,97 a 4,49:1). */}
                {contagem.data && <span className="numero text-xs">{numero(contagem.data[f] ?? 0)}</span>}
              </span>
            ),
          }))}
        />
      </div>
      <BarraVerticaisLote entidade="cadastros.parceiro" ids={lote.ids} aoLimpar={lote.limpar} chaveLista="/cadastros/parceiros" />
      <Tabela<Parceiro>
        tela="cadastros.pessoas"
        caminho="/cadastros/parceiros"
        params={params}
        filtros={{ situacao, vertical }}
        aoAplicarFiltros={(f) => {
          setSituacao(aceitoDaUrl(String(f.situacao ?? ''), SITUACOES_DA_OPERACAO));
          setVertical(uuidDaUrl(String(f.vertical ?? '')));
        }}
        filtrosExtra={
          <>
            <div className="w-full sm:w-56">
              <Selecao
                aria-label={t('situacoes.rotulo')}
                opcoes={SITUACOES_DA_OPERACAO.map((x) => ({ valor: x, rotulo: t(`situacoes.${x}`) }))}
                vazio={t('situacoes.todas')}
                value={situacao}
                onChange={(e) => setSituacao(e.target.value)}
              />
            </div>
            <FiltroVertical valor={vertical} aoMudar={setVertical} />
          </>
        }
        selecao={lote.selecao}
        colunas={colunas}
        entidade="cadastros.parceiro"
        aoClicar={(p) => navegar(`/cadastros/cadastros/parceiro/${p.id}`)}
        vazio={<Vazio texto={total === 0 && filtro !== 'todos' && (contagem.data?.todos ?? 0) > 0 ? t('vazio.filtro') : t('vazio.parceiros')} />}
      />
      {escolher && (
        <EscolherPapel
          aberto
          aoFechar={() => setEscolher(false)}
          inicial={papelDoFiltro(filtro)}
          aoEscolher={(papel) => {
            setEscolher(false);
            setNovo({ papel, empresa: filtro === 'empresa' || papel === 'fornecedor' });
          }}
        />
      )}
      <FormParceiro aberto={!!novo} aoFechar={() => setNovo(null)} papelNovo={novo?.papel} empresa={novo?.empresa} aoSalvar={(id) => navegar(`/cadastros/cadastros/parceiro/${id}`)} />
      <ImportarCsv aberto={importar} aoFechar={() => setImportar(false)} />
    </>
  );
}
